import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/documentation/documentation/theme/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Synopsis`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npx -- <pkg>[@<version>] [args...]
npx --package=<pkg>[@<version>] -- <cmd> [args...]
npx -c '<cmd> [args...]'
npx --package=foo -c '<cmd> [args...]'
`}</code></pre>
    <h3>{`Description`}</h3>
    <p>{`This command allows you to run an arbitrary command from an npm package
(either one installed locally, or fetched remotely), in a similar context
as running it via `}<inlineCode parentName="p">{`npm run`}</inlineCode>{`.`}</p>
    <p>{`Whatever packages are specified by the `}<inlineCode parentName="p">{`--package`}</inlineCode>{` option will be
provided in the `}<inlineCode parentName="p">{`PATH`}</inlineCode>{` of the executed command, along with any locally
installed package executables.  The `}<inlineCode parentName="p">{`--package`}</inlineCode>{` option may be
specified multiple times, to execute the supplied command in an environment
where all specified packages are available.`}</p>
    <p>{`If any requested packages are not present in the local project
dependencies, then they are installed to a folder in the npm cache, which
is added to the `}<inlineCode parentName="p">{`PATH`}</inlineCode>{` environment variable in the executed process.  A
prompt is printed (which can be suppressed by providing either `}<inlineCode parentName="p">{`--yes`}</inlineCode>{` or
`}<inlineCode parentName="p">{`--no`}</inlineCode>{`).`}</p>
    <p>{`Package names provided without a specifier will be matched with whatever
version exists in the local project.  Package names with a specifier will
only be considered a match if they have the exact same name and version as
the local dependency.`}</p>
    <p>{`If no `}<inlineCode parentName="p">{`-c`}</inlineCode>{` or `}<inlineCode parentName="p">{`--call`}</inlineCode>{` option is provided, then the positional arguments
are used to generate the command string.  If no `}<inlineCode parentName="p">{`--package`}</inlineCode>{` options
are provided, then npm will attempt to determine the executable name from
the package specifier provided as the first positional argument according
to the following heuristic:`}</p>
    <ul>
      <li parentName="ul">{`If the package has a single entry in its `}<inlineCode parentName="li">{`bin`}</inlineCode>{` field in `}<inlineCode parentName="li">{`package.json`}</inlineCode>{`,
or if all entries are aliases of the same command, then that command
will be used.`}</li>
      <li parentName="ul">{`If the package has multiple `}<inlineCode parentName="li">{`bin`}</inlineCode>{` entries, and one of them matches the
unscoped portion of the `}<inlineCode parentName="li">{`name`}</inlineCode>{` field, then that command will be used.`}</li>
      <li parentName="ul">{`If this does not result in exactly one option (either because there are
no bin entries, or none of them match the `}<inlineCode parentName="li">{`name`}</inlineCode>{` of the package), then
`}<inlineCode parentName="li">{`npm exec`}</inlineCode>{` exits with an error.`}</li>
    </ul>
    <p>{`To run a binary `}<em parentName="p">{`other than`}</em>{` the named binary, specify one or more
`}<inlineCode parentName="p">{`--package`}</inlineCode>{` options, which will prevent npm from inferring the package from
the first command argument.`}</p>
    <h3><inlineCode parentName="h3">{`npx`}</inlineCode>{` vs `}<inlineCode parentName="h3">{`npm exec`}</inlineCode></h3>
    <p>{`When run via the `}<inlineCode parentName="p">{`npx`}</inlineCode>{` binary, all flags and options `}<em parentName="p">{`must`}</em>{` be set prior to
any positional arguments.  When run via `}<inlineCode parentName="p">{`npm exec`}</inlineCode>{`, a double-hyphen `}<inlineCode parentName="p">{`--`}</inlineCode>{`
flag can be used to suppress npm's parsing of switches and options that
should be sent to the executed command.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ npx foo@latest bar --package=@npmcli/foo
`}</code></pre>
    <p>{`In this case, npm will resolve the `}<inlineCode parentName="p">{`foo`}</inlineCode>{` package name, and run the
following command:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ foo bar --package=@npmcli/foo
`}</code></pre>
    <p>{`Since the `}<inlineCode parentName="p">{`--package`}</inlineCode>{` option comes `}<em parentName="p">{`after`}</em>{` the positional arguments, it is
treated as an argument to the executed command.`}</p>
    <p>{`In contrast, due to npm's argument parsing logic, running this command is
different:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ npm exec foo@latest bar --package=@npmcli/foo
`}</code></pre>
    <p>{`In this case, npm will parse the `}<inlineCode parentName="p">{`--package`}</inlineCode>{` option first, resolving the
`}<inlineCode parentName="p">{`@npmcli/foo`}</inlineCode>{` package.  Then, it will execute the following command in that
context:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ foo@latest bar
`}</code></pre>
    <p>{`The double-hyphen character is recommended to explicitly tell npm to stop
parsing command line options and switches.  The following command would
thus be equivalent to the `}<inlineCode parentName="p">{`npx`}</inlineCode>{` command above:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ npm exec -- foo@latest bar --package=@npmcli/foo
`}</code></pre>
    <h3>{`Examples`}</h3>
    <p>{`Run the version of `}<inlineCode parentName="p">{`tap`}</inlineCode>{` in the local dependencies, with the provided
arguments:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ npm exec -- tap --bail test/foo.js
$ npx tap --bail test/foo.js
`}</code></pre>
    <p>{`Run a command `}<em parentName="p">{`other than`}</em>{` the command whose name matches the package name
by specifying a `}<inlineCode parentName="p">{`--package`}</inlineCode>{` option:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ npm exec --package=foo -- bar --bar-argument
# ~ or ~
$ npx --package=foo bar --bar-argument
`}</code></pre>
    <p>{`Run an arbitrary shell script, in the context of the current project:`}</p>
    <pre><code parentName="pre" {...{}}>{`$ npm x -c 'eslint && say "hooray, lint passed"'
$ npx -c 'eslint && say "hooray, lint passed"'
`}</code></pre>
    <h3>{`Compatibility with Older npx Versions`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`npx`}</inlineCode>{` binary was rewritten in npm v7.0.0, and the standalone `}<inlineCode parentName="p">{`npx`}</inlineCode>{`
package deprecated at that time.  `}<inlineCode parentName="p">{`npx`}</inlineCode>{` uses the `}<inlineCode parentName="p">{`npm exec`}</inlineCode>{`
command instead of a separate argument parser and install process, with
some affordances to maintain backwards compatibility with the arguments it
accepted in previous versions.`}</p>
    <p>{`This resulted in some shifts in its functionality:`}</p>
    <ul>
      <li parentName="ul">{`Any `}<inlineCode parentName="li">{`npm`}</inlineCode>{` config value may be provided.`}</li>
      <li parentName="ul">{`To prevent security and user-experience problems from mistyping package
names, `}<inlineCode parentName="li">{`npx`}</inlineCode>{` prompts before installing anything.  Suppress this
prompt with the `}<inlineCode parentName="li">{`-y`}</inlineCode>{` or `}<inlineCode parentName="li">{`--yes`}</inlineCode>{` option.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`--no-install`}</inlineCode>{` option is deprecated, and will be converted to `}<inlineCode parentName="li">{`--no`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Shell fallback functionality is removed, as it is not advisable.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`-p`}</inlineCode>{` argument is a shorthand for `}<inlineCode parentName="li">{`--parseable`}</inlineCode>{` in npm, but shorthand
for `}<inlineCode parentName="li">{`--package`}</inlineCode>{` in npx.  This is maintained, but only for the `}<inlineCode parentName="li">{`npx`}</inlineCode>{`
executable.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`--ignore-existing`}</inlineCode>{` option is removed.  Locally installed bins are
always present in the executed process `}<inlineCode parentName="li">{`PATH`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`--npm`}</inlineCode>{` option is removed.  `}<inlineCode parentName="li">{`npx`}</inlineCode>{` will always use the `}<inlineCode parentName="li">{`npm`}</inlineCode>{` it ships
with.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`--node-arg`}</inlineCode>{` and `}<inlineCode parentName="li">{`-n`}</inlineCode>{` options are removed.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`--always-spawn`}</inlineCode>{` option is redundant, and thus removed.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`--shell`}</inlineCode>{` option is replaced with `}<inlineCode parentName="li">{`--script-shell`}</inlineCode>{`, but maintained
in the `}<inlineCode parentName="li">{`npx`}</inlineCode>{` executable for backwards compatibility.`}</li>
    </ul>
    <h3>{`See Also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/commands/npm-run-script"
        }}>{`npm run-script`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/using-npm/scripts"
        }}>{`npm scripts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/commands/npm-test"
        }}>{`npm test`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/commands/npm-start"
        }}>{`npm start`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/commands/npm-restart"
        }}>{`npm restart`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/commands/npm-stop"
        }}>{`npm stop`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/commands/npm-config"
        }}>{`npm config`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v9/commands/npm-exec"
        }}>{`npm exec`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      